import type { FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/ui/use-toast";

const passwordFormSchema = z
	.object({
		currentPassword: z
			.string()
			.min(1, { message: "現在のパスワードを入力してください" }),
		newPassword: z
			.string()
			.min(8, { message: "パスワードは8文字以上で入力してください" })
			.regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, {
				message: "パスワードには大文字、小文字、数字を含める必要があります",
			}),
		confirmPassword: z.string(),
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: "新しいパスワードと確認用パスワードが一致しません",
		path: ["confirmPassword"],
	});

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

export const PasswordForm: FC = () => {
	const { toast } = useToast();

	const form = useForm<PasswordFormValues>({
		resolver: zodResolver(passwordFormSchema),
	});

	const onSubmit = async (data: PasswordFormValues) => {
		try {
			// TODO: パスワード変更のAPI呼び出し
			toast({
				title: "パスワードを変更しました",
				description: "新しいパスワードが設定されました",
			});
			form.reset();
		} catch (error) {
			toast({
				title: "エラーが発生しました",
				description: "パスワードの変更に失敗しました",
				variant: "destructive",
			});
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="currentPassword"
					render={({ field }) => (
						<FormItem>
							<FormLabel>現在のパスワード</FormLabel>
							<FormControl>
								<Input type="password" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="newPassword"
					render={({ field }) => (
						<FormItem>
							<FormLabel>新しいパスワード</FormLabel>
							<FormControl>
								<Input type="password" {...field} />
							</FormControl>
							<FormDescription>
								8文字以上で、大文字、小文字、数字を含める必要があります
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="confirmPassword"
					render={({ field }) => (
						<FormItem>
							<FormLabel>新しいパスワード（確認）</FormLabel>
							<FormControl>
								<Input type="password" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<Button type="submit">パスワードを変更</Button>
			</form>
		</Form>
	);
};
